import { authApi, baseApi } from 'utils/apiUtil';
import {
  generateCodeChallenge,
  generateRandomString,
  httpM as httpM,
} from 'utils/httpUtil';

const employeeSignUpURL = `${baseApi}/v2/mobile/signup`;
const authFlow = `${baseApi}/v1/logins/flow`;
const featureAccessURL = `${baseApi}/v2/mobile/feature-access`;
const tokenURL = `${authApi}/oauth2/token`;
const authorizeURL = `${authApi}/oauth2/authorize`;
const resetPasswordURL = `${baseApi}/v2/mobile/password-reset`;
const employeeDetails = `${baseApi}/v1/employees`;
const loginByIndividualId = `${baseApi}/v1/logins/individuals`;

export const EXPIRE_TIME = 'expire_time'; // expire key for local storage.
export const LOGGED_IN_USER_EMAIL = 'logged_user'; // expire key for local storage.

export const getFeatureAccess = async (email: string) => {
  try {
    const response = await httpM.post(featureAccessURL, { email: email });
    return Promise.resolve(response);
  } catch (error) {
    return Promise.reject(error);
  }
};

export const requestPasswordReset = async (
  email: string,
  redirectUrl: string
) => {
  try {
    const response = await httpM.post(`${resetPasswordURL}/token/request`, {
      email: email,
      isDBGv2: true,
      redirectUrl: redirectUrl,
    });
    return Promise.resolve(response);
  } catch (error) {
    return Promise.reject(error);
  }
};

export const passwordResetToken = async (
  token: string,
  newPassword: string
) => {
  try {
    const response = await httpM.post(`${resetPasswordURL}`, {
      token: token,
      newPassword: newPassword,
    });
    return Promise.resolve(response);
  } catch (error) {
    return Promise.reject(error);
  }
};

export const validatePasswordResetToken = async (token: string) => {
  try {
    const response = await httpM.post(`${resetPasswordURL}/token/validate`, {
      token: token,
    });
    return Promise.resolve(response);
  } catch (error) {
    return Promise.reject(error);
  }
};

export const getAuthFlow = async (email: string, benguideUrl: string) => {
  try {
    const response = await httpM.post(authFlow, {
      username: email,
      benguideUrl: benguideUrl,
    });
    return Promise.resolve(response);
  } catch (error) {
    return Promise.reject(error);
  }
};

export const whoamiInduvidual = async () => {
  const config = {
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
    },
  };
  return await httpM.get(`${baseApi}/v1/individuals/whoami`, config);
};

// Support for OAuth2 authorization code flow with PKCE
export const initiateOAuth2Login = async (
  email: string,
  planYearId?: string,
  benguideUrl?: string
) => {
  // Generate code verifier (random string between 43-128 chars)
  const codeVerifier = generateRandomString(64);

  // Store code verifier in local storage for later use
  localStorage.setItem('code_verifier', codeVerifier);

  // Store planYearId for use in callback
  if (planYearId) {
    localStorage.setItem('planYearId', planYearId);
  }

  let benguidePath = '/';
  if (benguideUrl) {
    // Remove 'benguideV2' prefix if it exists
    if (benguideUrl.startsWith('benguideV2')) {
      benguideUrl = benguideUrl.substring(10); // 'benguideV2' is 10 characters
    }
    localStorage.setItem('benguideUrl', benguideUrl);
    benguidePath = encodeURIComponent(benguideUrl);
  }

  // Generate code challenge from verifier
  const codeChallenge = await generateCodeChallenge(codeVerifier);

  // Build authorization URL with PKCE parameters
  const redirectUri = encodeURIComponent(
    `${window.location.origin}/auth/callback`
  );

  // Build authorization URL with PKCE parameters
  const fallBackUri = encodeURIComponent(
    `${window.location.origin}${benguidePath}`
  );

  const authUrl =
    `${authorizeURL}?` +
    `response_type=code` +
    `&client_id=up` +
    `&redirect_uri=${redirectUri}` +
    `&scope=USER` +
    `&code_challenge=${codeChallenge}` +
    `&code_challenge_method=S256` +
    `&email=${encodeURIComponent(email)}` +
    `&fall_back_uri=${fallBackUri}` +
    `&login_from=DBG`;

  window.location.href = authUrl;
};

/**
 * Exchange authorization code for access token using PKCE
 * @param {string} code The authorization code received from the OAuth server
 * @param {string} codeVerifier The code verifier generated during login initiation
 * @return {Promise<any>} Promise with the token response
 */
export const exchangeCodeForToken = async (
  code: string,
  codeVerifier: string
) => {
  try {
    // Prepare redirect URI - must be the same as the one used for authorization
    const redirectUri = `${window.location.origin}/auth/callback`;

    // Prepare request body
    const params = new URLSearchParams();
    params.append('grant_type', 'authorization_code');
    params.append('client_id', 'up');
    params.append('code_verifier', codeVerifier);
    params.append('code', code);
    params.append('redirect_uri', redirectUri);

    const config = {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
        Origin: window.location.origin,
      },
    };

    // Clear the code verifier after using it
    localStorage.removeItem('code_verifier');

    // Make token request
    const response = await httpM.post(tokenURL, params.toString(), config);

    // Handle successful token response
    if (response.data && response.data.token_type) {
      return response;
    }

    return Promise.reject(new Error('Invalid token response'));
  } catch (error) {
    return Promise.reject(error);
  }
};

export const revokeUser = async () => {
  try {
    const response = await httpM.post(`${authApi}/revoke`, {});
    return Promise.resolve(response);
  } catch (error) {
    return Promise.reject(error);
  }
};

export const validateTokenAndGetUserRole = async (token: string) => {
  try {
    const response = await httpM.post(`${employeeSignUpURL}/token/validate`, {
      token: token,
    });
    return Promise.resolve(response);
  } catch (error) {
    return Promise.reject(error);
  }
};

export const verifyEmail = async (email: string) => {
  try {
    const response = await httpM.post(
      `${employeeSignUpURL}/personal/email/verify`,
      {
        email: email,
      }
    );
    return Promise.resolve(response);
  } catch (error) {
    return Promise.reject(error);
  }
};

export const requestSignUp = async (
  email: string,
  redirectUrl: string,
  domain?: string | undefined
) => {
  try {
    const response = await httpM.post(
      `${employeeSignUpURL}/employee/token/request`,
      {
        username: email,
        employerDomain: domain,
        isDBGv2: true,
        redirectUrl: redirectUrl,
      }
    );

    return Promise.resolve(response);
  } catch (error) {
    return Promise.reject(error);
  }
};

export const register = async (
  email: string,
  token: string,
  password: string,
  firstName?: string,
  lastName?: string,
  isSelfDeclared?: boolean
) => {
  try {
    const response = await httpM.post(`${employeeSignUpURL}`, {
      token: token,
      email: email,
      firstName: firstName,
      lastName: lastName,
      password: password,
      isSelfDeclared: isSelfDeclared,
    });

    return Promise.resolve(response);
  } catch (error) {
    return Promise.reject(error);
  }
};

export const ssoRegister = async (
  email: string,
  token: string,
  firstName?: string,
  lastName?: string,
  isSelfDeclared?: boolean
) => {
  try {
    const response = await httpM.post(
      `${employeeSignUpURL}/employee/sso-register`,
      {
        email: email,
        token: token,
        firstName: firstName,
        lastName: lastName,
        isSelfDeclared: isSelfDeclared,
      }
    );

    return Promise.resolve(response);
  } catch (error) {
    return Promise.reject(error);
  }
};

export const loginByInduvidualId = async (individualId: string) => {
  try {
    const response = await httpM.get(`${loginByIndividualId}/${individualId}`);

    return Promise.resolve(response);
  } catch (error) {
    return Promise.reject(error);
  }
};

export const getDependentsDetails = async (
  employeeId: string,
  planYearId: string
) => {
  try {
    const response = await httpM.get(
      `${employeeDetails}/${employeeId}/dependents`,
      {
        params: {
          'plan-year-id': planYearId,
        },
      }
    );
    return Promise.resolve(response);
  } catch (error) {
    return Promise.reject(error);
  }
};

export const getEmployeeDetails = async (employeeId: string) => {
  try {
    const response = await httpM.get(`${employeeDetails}/${employeeId}`);
    return Promise.resolve(response);
  } catch (error) {
    return Promise.reject(error);
  }
};
